import { supabase } from '@/lib/supabase/client'

export interface UploadedFileRef {
  storagePath: string
  bucket: string
  originalName: string
  mimeType: string
  sizeBytes: number
}

export async function uploadFileToStorage(
  file: File,
  projectId: string,
  recordId: string,
  formId: string,
  fieldKey: string,
  submissionId: string | null,
): Promise<UploadedFileRef> {
  const path = `${projectId}/records/${recordId}/forms/${formId}/submissions/${submissionId ?? 'pending'}/${fieldKey}/${file.name}`
  const { error } = await supabase.storage
    .from('project-files')
    .upload(path, file, { upsert: true })
  if (error) throw error
  return {
    storagePath: path,
    bucket: 'project-files',
    originalName: file.name,
    mimeType: file.type || 'application/octet-stream',
    sizeBytes: file.size,
  }
}

export async function insertFileRef(
  submissionId: string,
  fieldKey: string,
  ref: UploadedFileRef,
  userId: string,
): Promise<void> {
  try {
    await supabase.from('file_refs').insert({
      submission_id: submissionId,
      field_key: fieldKey,
      storage_bucket: ref.bucket,
      storage_path: ref.storagePath,
      original_name: ref.originalName,
      mime_type: ref.mimeType,
      size_bytes: ref.sizeBytes,
      uploaded_by: userId,
    })
  } catch (err) {
    // Deliberately non-throwing (the file is already in storage and callers do
    // not handle a failure here), but it must not be *silent*: without the row
    // the upload is invisible to the app, which looks like data loss to the
    // user and leaves an orphan in the bucket. Surfacing it is the minimum;
    // this should probably reconcile or retry instead of shrugging.
    console.error('insertFileRef failed — file is in storage with no file_refs row', {
      submissionId,
      fieldKey,
      storagePath: ref.storagePath,
      err,
    })
  }
}

export async function getSignedUrl(bucket: string, storagePath: string): Promise<string | null> {
  const { data, error } = await supabase.storage
    .from(bucket)
    .createSignedUrl(storagePath, 60)
  if (error || !data?.signedUrl) return null
  return data.signedUrl
}
